import {
  StandardButton,
  StandardSearchBox,
  TextAutoComplete,
  StandardScreen,
  StandardDropdown,
  StatusMessage,
  PageLayout,
} from "components/Layout";
import * as React from "react";

import {
  getRegionNames,
  getClientAttributes,
  registerSPHost,
  updateClientAttributes,
  getExistingClientNames,
  getSPHostsByClientName,
} from "../components/Requests/Requests";

const accountTypeOptions = [
  { key: "Intapp", text: "Intapp" },
  { key: "Partner", text: "Partner" },
  { key: "Client", text: "Client" },
];

const hostSiteTypeOptions = [
  { key: "prod", text: "Prod" },
  { key: "uat", text: "UAT" },
  { key: "dev", text: "Dev" },
];

function ClientNameResultsList({ results, onSelect, disabled }) {
  return (
    <div style={{ marginTop: "10px" }}>
      <p style={{ marginBottom: "8px" }}>
        Select an SPHost URL to load its details:
      </p>
      <div
        style={{
          border: "1px solid #ccc",
          borderRadius: "2px",
          overflow: "hidden",
        }}
      >
        {results.map((result, index) => (
          <ClientNameResultRow
            key={`${result.spHostUrl}|${result.regionName}`}
            result={result}
            index={index}
            onSelect={onSelect}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

class ClientNameResultRow extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hovered: false };
  }

  render() {
    const { result, index, onSelect, disabled } = this.props;
    const { hovered } = this.state;
    return (
      <div
        onClick={() => !disabled && onSelect(result)}
        onMouseEnter={() => !disabled && this.setState({ hovered: true })}
        onMouseLeave={() => this.setState({ hovered: false })}
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "8px 10px",
          cursor: disabled ? "default" : "pointer",
          borderTop: index === 0 ? "none" : "1px solid #eee",
          backgroundColor: hovered && !disabled ? "#f3f2f1" : "#fff",
        }}
      >
        <span>{result.spHostUrl}</span>
        <span
          style={{ color: "#666", marginLeft: "16px", whiteSpace: "nowrap" }}
        >
          {result.regionName}
        </span>
      </div>
    );
  }
}

class ManageClient extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      searching: false,
      saving: false,

      searchMessage: "",
      searchError: "",

      saveMessage: "",
      saveError: "",

      // Search inputs
      spHostUrlInput: "",
      regionNameInput: "",

      // Loaded data after search
      hasSearched: false,
      spHostExists: false,
      spHostUrl: "",
      clientName: "",
      existingClientNames: [],
      regionName: "",
      accountType: null,
      hostSiteType: null,

      // Dropdown options
      regionOptions: [],

      // URL validation warning
      urlWarningMessage: "",

      // Client name card - search
      clientNameSearchInput: "",
      clientNameSearching: false,
      clientNameSearchError: "",
      clientNameSearchWarning: "",
      clientNameResults: [],

      // Client name card - selected SPHost form
      cnCardHasSelected: false,
      cnCardSaving: false,
      cnCardSpHostUrl: "",
      cnCardRegionName: "",
      cnCardSpHostExists: false,
      cnCardClientName: "",
      cnCardAccountType: null,
      cnCardHostSiteType: null,
      cnCardSaveMessage: "",
      cnCardSaveError: "",
      cnCardSearchMessage: "",
      cnCardSearchMessageType: "success",
    };
  }

  componentDidMount() {
    this.loadRegionNames();
    this.loadExistingClientNames();
  }

  async loadRegionNames() {
    this.setState({ loading: true });
    try {
      const response = await getRegionNames(this.props.accessToken);
      if (response.ok) {
        const data = await response.json();
        this.setState({
          loading: false,
          regionOptions: data.map((item) => ({ key: item, text: item })),
        });
      } else {
        this.setState({
          loading: false,
          searchError: "Failed to load environment names",
        });
      }
    } catch (error) {
      this.setState({
        loading: false,
        searchError: "Failed to load environment names",
      });
    }
  }

  validateSharePointUrlPattern = (url) => {
    if (!url || url.trim() === "") {
      return "";
    }

    const urlLower = url.toLowerCase().trim();
    const sitePattern =
      /^https:\/\/[a-z0-9][a-z0-9-]*\.sharepoint\.com\/.+$/;

    if (sitePattern.test(urlLower)) {
      return "";
    }

    return "This URL does not match the standard SharePoint pattern (https://[tenant].sharepoint.com/...). Verify this is correct before proceeding.";
  };

  onSpHostUrlInputChange = (newValue) => {
    const value = newValue || "";
    this.setState({
      spHostUrlInput: value,
      searchError: "",
      searchMessage: "",
      saveError: "",
      saveMessage: "",
      urlWarningMessage: this.validateSharePointUrlPattern(value),
    });
  };

  onRegionInputChange = (event, item) => {
    this.setState({
      regionNameInput: item ? item.key : "",
      searchError: "",
      searchMessage: "",
      saveError: "",
      saveMessage: "",
    });
  };

  onSearch = async () => {
    const spHostUrl = (this.state.spHostUrlInput || "").trim();
    const regionName = this.state.regionNameInput;

    if (!spHostUrl) {
      this.setState({
        searchError: "Please enter a SPHostUrl",
        searchMessage: "",
      });
      return;
    }

    if (!regionName) {
      this.setState({
        searchError: "Please select an environment",
        searchMessage: "",
      });
      return;
    }

    this.setState({
      searching: true,
      searchError: "",
      searchMessage: "",
      saveError: "",
      saveMessage: "",
      hasSearched: false,
    });

    try {
      const response = await getClientAttributes(
        this.props.accessToken,
        spHostUrl,
        regionName,
      );

      if (response.ok) {
        // SPHost EXISTS
        const data = await response.json();

        this.setState({
          searching: false,
          hasSearched: true,
          spHostExists: true,
          spHostUrl: data.spHostUrl || spHostUrl,
          clientName: data.clientName || "",
          regionName: regionName,
          accountType: data.accountType || null,
          hostSiteType: data.hostSiteType || null,
          searchMessage:
            "SPHost registered. You can update the attributes below.",
        });
      } else if (response.status === 404) {
        // SPHost DOES NOT EXIST
        this.setState({
          searching: false,
          hasSearched: true,
          spHostExists: false,
          spHostUrl: spHostUrl,
          clientName: "",
          regionName: regionName,
          accountType: null,
          hostSiteType: null,
          searchMessage:
            "SPHost not registered. Please fill out the form below to register.",
        });
      } else {
        const errorText = await response.text();
        let errorMessage = "Failed to search for SPHost";
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        this.setState({
          searching: false,
          searchError: errorMessage,
          hasSearched: false,
        });
      }
    } catch (error) {
      this.setState({
        searching: false,
        searchError: "An error occurred while searching for the SPHost",
        hasSearched: false,
      });
    }
  };

  loadExistingClientNames = async () => {
    try {
      const response = await getExistingClientNames(this.props.accessToken);
      if (response.ok) {
        const data = await response.json();
        this.setState({ existingClientNames: data || [] });
      }
    } catch {
      this.setState({ existingClientNames: [] });
    }
  };

  onClientNameChange = (value) => {
    this.setState({
      clientName: value || "",
      saveError: "",
      saveMessage: "",
    });
  };

  onClientNameSelect = (value) => {
    this.setState({
      clientName: value || "",
      saveError: "",
      saveMessage: "",
    });
  };

  onAccountTypeChange = (event, item) => {
    this.setState({
      accountType: item ? item.key : null,
      saveError: "",
      saveMessage: "",
    });
  };

  onHostSiteTypeChange = (event, item) => {
    this.setState({
      hostSiteType: item ? item.key : null,
      saveError: "",
      saveMessage: "",
    });
  };

  onClientNameSearchInputChange = (value) => {
    this.setState({
      clientNameSearchInput: value || "",
      clientNameSearchError: "",
      clientNameResults: [],
    });
  };

  onSearchByClientName = async () => {
    const clientName = (this.state.clientNameSearchInput || "").trim();

    if (!clientName) {
      this.setState({ clientNameSearchError: "Please enter a client name" });
      return;
    }

    this.setState({
      clientNameSearching: true,
      clientNameSearchError: "",
      clientNameSearchWarning: "",
      clientNameResults: [],
    });

    try {
      const response = await getSPHostsByClientName(
        this.props.accessToken,
        clientName,
      );

      if (response.ok) {
        const data = await response.json();
        const results = data.results || [];
        const failedRegions = data.failedRegions || [];
        const failedWarning =
          failedRegions.length > 0
            ? `Results may be incomplete — could not reach: ${failedRegions.join(", ")}.`
            : "";
        if (results.length === 0) {
          this.setState({
            clientNameSearching: false,
            clientNameSearchError: "No SPHosts found for this client name",
            clientNameSearchWarning: failedWarning,
          });
        } else {
          this.setState({
            clientNameSearching: false,
            clientNameResults: results,
            clientNameSearchWarning: failedWarning,
          });
        }
      } else {
        const errorText = await response.text();
        let errorMessage = "Failed to search by client name";
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        this.setState({
          clientNameSearching: false,
          clientNameSearchError: errorMessage,
        });
      }
    } catch {
      this.setState({
        clientNameSearching: false,
        clientNameSearchError: "An error occurred while searching",
      });
    }
  };

  onSelectClientNameResult = async (result) => {
    this.setState({
      clientNameResults: [],
      clientNameSearchInput: "",
      clientNameSearchError: "",
      cnCardHasSelected: false,
      cnCardSaveMessage: "",
      cnCardSaveError: "",
      cnCardSearchMessage: "",
      cnCardSearchMessageType: "success",
    });

    try {
      const response = await getClientAttributes(
        this.props.accessToken,
        result.spHostUrl,
        result.regionName,
      );

      if (response.ok) {
        const data = await response.json();
        this.setState({
          cnCardHasSelected: true,
          cnCardSpHostExists: true,
          cnCardSpHostUrl: data.spHostUrl || result.spHostUrl,
          cnCardRegionName: result.regionName,
          cnCardClientName: data.clientName || "",
          cnCardAccountType: data.accountType || null,
          cnCardHostSiteType: data.hostSiteType || null,
          cnCardSearchMessage:
            "SPHost loaded. You can update the attributes below.",
          cnCardSearchMessageType: "success",
        });
      } else if (response.status === 404) {
        this.setState({
          cnCardHasSelected: true,
          cnCardSpHostExists: false,
          cnCardSpHostUrl: result.spHostUrl,
          cnCardRegionName: result.regionName,
          cnCardClientName: "",
          cnCardAccountType: null,
          cnCardHostSiteType: null,
          cnCardSearchMessage:
            "No client matching the entered details was found.",
          cnCardSearchMessageType: "warning",
        });
      } else {
        const errorText = await response.text();
        let errorMessage = "Failed to load SPHost details";
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        this.setState({ cnCardSaveError: errorMessage });
      }
    } catch {
      this.setState({
        cnCardSaveError: "An error occurred while loading SPHost details",
      });
    }
  };

  onCnCardClientNameChange = (value) => {
    this.setState({
      cnCardClientName: value || "",
      cnCardSaveError: "",
      cnCardSaveMessage: "",
    });
  };

  onCnCardClientNameSelect = (value) => {
    this.setState({
      cnCardClientName: value || "",
      cnCardSaveError: "",
      cnCardSaveMessage: "",
    });
  };

  onCnCardAccountTypeChange = (event, item) => {
    this.setState({
      cnCardAccountType: item ? item.key : null,
      cnCardSaveError: "",
      cnCardSaveMessage: "",
    });
  };

  onCnCardHostSiteTypeChange = (event, item) => {
    this.setState({
      cnCardHostSiteType: item ? item.key : null,
      cnCardSaveError: "",
      cnCardSaveMessage: "",
    });
  };

  onCnCardSave = async () => {
    const {
      cnCardSpHostUrl,
      cnCardRegionName,
      cnCardClientName,
      cnCardAccountType,
      cnCardHostSiteType,
      cnCardSpHostExists,
    } = this.state;

    if (!cnCardClientName.trim()) {
      this.setState({
        cnCardSaveError: "Client Name is required",
        cnCardSaveMessage: "",
      });
      return;
    }
    if (!cnCardAccountType) {
      this.setState({
        cnCardSaveError: "Account Type is required",
        cnCardSaveMessage: "",
      });
      return;
    }
    if (!cnCardHostSiteType) {
      this.setState({
        cnCardSaveError: "Host Site Type is required",
        cnCardSaveMessage: "",
      });
      return;
    }

    const requestBody = {
      spHostUrl: cnCardSpHostUrl.trim(),
      regionName: cnCardRegionName,
      clientName: cnCardClientName.trim(),
      accountType: cnCardAccountType,
      hostSiteType: cnCardHostSiteType,
    };

    this.setState({
      cnCardSaving: true,
      cnCardSaveError: "",
      cnCardSaveMessage: "",
    });

    try {
      const response = cnCardSpHostExists
        ? await updateClientAttributes(this.props.accessToken, requestBody)
        : await registerSPHost(this.props.accessToken, requestBody);

      if (response.ok) {
        const data = await response.json();
        this.setState({
          cnCardSaving: false,
          cnCardSpHostExists: true,
          cnCardSaveMessage:
            data.message || "Client attributes successfully saved",
        });
      } else {
        const errorText = await response.text();
        let errorMessage = "Failed to save client attributes";
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        this.setState({ cnCardSaving: false, cnCardSaveError: errorMessage });
      }
    } catch {
      this.setState({
        cnCardSaving: false,
        cnCardSaveError: "An error occurred while saving client attributes",
      });
    }
  };

  validateForm = () => {
    const { spHostUrl, clientName, regionName, accountType, hostSiteType } =
      this.state;

    if (!spHostUrl || !spHostUrl.trim()) {
      return "SPHostUrl is required";
    }
    if (!clientName || !clientName.trim()) {
      return "Client Name is required";
    }
    if (!regionName) {
      return "Environment is required";
    }
    if (!accountType) {
      return "Account Type is required";
    }
    if (!hostSiteType) {
      return "Host Site Type is required";
    }

    return null;
  };

  onSave = async () => {
    const validationError = this.validateForm();
    if (validationError) {
      this.setState({ saveError: validationError, saveMessage: "" });
      return;
    }

    const {
      spHostUrl,
      clientName,
      regionName,
      accountType,
      hostSiteType,
      spHostExists,
    } = this.state;

    const requestBody = {
      spHostUrl: spHostUrl.trim(),
      regionName: regionName,
      clientName: clientName.trim(),
      accountType: accountType,
      hostSiteType: hostSiteType,
    };

    this.setState({
      saving: true,
      saveError: "",
      saveMessage: "",
      searchMessage: "",
    });

    try {
      const response = spHostExists
        ? await updateClientAttributes(this.props.accessToken, requestBody)
        : await registerSPHost(this.props.accessToken, requestBody);

      if (response.ok) {
        const data = await response.json();
        this.setState({
          saving: false,
          spHostExists: true,
          saveMessage: data.message || "Client attributes successfully saved",
          searchMessage: "",
        });
      } else {
        const errorText = await response.text();
        let errorMessage = "Failed to save client attributes";
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        this.setState({
          saving: false,
          saveError: errorMessage,
          searchMessage: "",
        });
      }
    } catch (error) {
      this.setState({
        saving: false,
        saveError: "An error occurred while saving client attributes",
        searchMessage: "",
      });
    }
  };

  render() {
    const {
      loading,
      searching,
      saving,
      searchMessage,
      searchError,
      saveMessage,
      saveError,
      urlWarningMessage,
      spHostUrlInput,
      regionNameInput,
      hasSearched,
      clientName,
      accountType,
      hostSiteType,
      regionOptions,
      clientNameSearchInput,
      clientNameSearching,
      clientNameSearchError,
      clientNameSearchWarning,
      clientNameResults,
      cnCardHasSelected,
      cnCardSaving,
      cnCardSpHostUrl,
      cnCardRegionName,
      cnCardClientName,
      cnCardAccountType,
      cnCardHostSiteType,
      cnCardSaveMessage,
      cnCardSaveError,
      cnCardSearchMessage,
      cnCardSearchMessageType,
    } = this.state;

    const isFormDisabled = saving || searching || !hasSearched;
    const isSearchDisabled = searching || saving;
    const isCnCardFormDisabled = cnCardSaving || !cnCardHasSelected;

    return (
      <PageLayout>
        {/* Search by SPHost URL */}
        <StandardScreen
          title="Manage Client by SPHost URL"
          description="Search for an SPHost by URL to view or update its client attributes."
          loading={loading}
        >
          <StatusMessage type="warning">
            <strong>Important!</strong> For Intapp dev/qa/support/services host
            sites, use the BypassSPHostRegistration feature flag - this allows
            ICP to be installed without requiring pre-registration. Do not
            enable this flag for client or partner tenants.
          </StatusMessage>

          {/* Search Section */}
          <div style={{ marginBottom: "20px" }}>
            <StandardSearchBox
              label="SPHostUrl"
              placeholder="Enter SPHostUrl (e.g., https://tenant.sharepoint.com)"
              value={spHostUrlInput}
              onChange={this.onSpHostUrlInputChange}
              onSearch={this.onSearch}
              disabled={isSearchDisabled}
            />
            {urlWarningMessage && (
              <StatusMessage type="warning" style={{ marginTop: "10px" }}>
                {urlWarningMessage}
              </StatusMessage>
            )}

            <StandardDropdown
              label="Environment"
              selectedKey={regionNameInput}
              onChange={this.onRegionInputChange}
              placeholder="Select Environment"
              options={regionOptions}
              disabled={isSearchDisabled}
              style={{ marginTop: "10px" }}
            />

            <StandardButton
              text={searching ? "Searching..." : "Search"}
              onClick={this.onSearch}
              disabled={
                isSearchDisabled || !spHostUrlInput.trim() || !regionNameInput
              }
              style={{ marginTop: "10px" }}
            />
          </div>

          {/* Status Messages */}
          {searchError && (
            <StatusMessage type="error">{searchError}</StatusMessage>
          )}
          {searchMessage && (
            <StatusMessage type="success">{searchMessage}</StatusMessage>
          )}

          {/* Form Section*/}
          {hasSearched && (
            <div
              style={{
                marginTop: "30px",
                borderTop: "1px solid #ccc",
                paddingTop: "20px",
              }}
            >
              <TextAutoComplete
                label="Client Name"
                placeholder="Enter the customer's official company name"
                value={clientName}
                onChange={this.onClientNameChange}
                onSelect={this.onClientNameSelect}
                options={this.state.existingClientNames}
                disabled={isFormDisabled}
                description="Use the customer's official company name as found in OP4i"
              />

              <StandardDropdown
                label="Account Type"
                selectedKey={accountType}
                onChange={this.onAccountTypeChange}
                placeholder="Select account type"
                options={accountTypeOptions}
                disabled={isFormDisabled}
                description="Select Intapp, Partner, or Client"
              />

              <StandardDropdown
                label="Host Site Type"
                selectedKey={hostSiteType}
                onChange={this.onHostSiteTypeChange}
                placeholder="Select Host Site Type"
                options={hostSiteTypeOptions}
                disabled={isFormDisabled}
                description="Select Prod, UAT, or Dev"
              />

              <StandardButton
                text={saving ? "Saving..." : "Save"}
                onClick={this.onSave}
                disabled={
                  isFormDisabled ||
                  !clientName.trim() ||
                  !accountType ||
                  !hostSiteType
                }
                primary
                style={{ marginTop: "20px" }}
              />

              {saveError && (
                <StatusMessage type="error">{saveError}</StatusMessage>
              )}
              {saveMessage && (
                <StatusMessage type="success">{saveMessage}</StatusMessage>
              )}
            </div>
          )}
        </StandardScreen>

        {/*Search by Client Name */}
        <StandardScreen
          title="Manage Client by Client Name"
          description="Search for SPHost URLs linked to a client name, then select one to view and edit its details."
        >
          {/* Client name search */}
          <div style={{ marginBottom: "20px" }}>
            <TextAutoComplete
              label="Client Name"
              placeholder="Enter a client name"
              value={clientNameSearchInput}
              onChange={this.onClientNameSearchInputChange}
              onSelect={(value) => {
                this.setState({ clientNameSearchInput: value });
              }}
              options={this.state.existingClientNames}
              disabled={clientNameSearching}
            />

            <StandardButton
              text={clientNameSearching ? "Searching..." : "Search"}
              onClick={this.onSearchByClientName}
              disabled={clientNameSearching || !clientNameSearchInput.trim()}
              style={{ marginTop: "10px" }}
            />

            {clientNameSearchError && (
              <StatusMessage type="error" style={{ marginTop: "10px" }}>
                {clientNameSearchError}
              </StatusMessage>
            )}
            {clientNameSearchWarning && (
              <StatusMessage type="warning" style={{ marginTop: "10px" }}>
                {clientNameSearchWarning}
              </StatusMessage>
            )}

            {clientNameResults.length > 0 && (
              <ClientNameResultsList
                results={clientNameResults}
                onSelect={this.onSelectClientNameResult}
                disabled={clientNameSearching}
              />
            )}
          </div>

          {/* Form populated after selecting an SPHost */}
          {cnCardHasSelected && (
            <div
              style={{
                marginTop: "30px",
                borderTop: "1px solid #ccc",
                paddingTop: "20px",
              }}
            >
              {cnCardSearchMessage && (
                <StatusMessage type={cnCardSearchMessageType} style={{ marginBottom: "10px" }}>
                  {cnCardSearchMessage}
                </StatusMessage>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "32px",
                  backgroundColor: "#f3f2f1",
                  border: "1px solid #e1dfdd",
                  borderRadius: "2px",
                  padding: "10px 14px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              >
                <div>
                  <div
                    style={{
                      color: "#605e5c",
                      fontSize: "12px",
                      marginBottom: "2px",
                    }}
                  >
                    SPHost URL
                  </div>
                  <div>{cnCardSpHostUrl}</div>
                </div>
                <div>
                  <div
                    style={{
                      color: "#605e5c",
                      fontSize: "12px",
                      marginBottom: "2px",
                    }}
                  >
                    Environment
                  </div>
                  <div>{cnCardRegionName}</div>
                </div>
              </div>

              <TextAutoComplete
                label="Client Name"
                placeholder="Enter the customer's official company name"
                value={cnCardClientName}
                onChange={this.onCnCardClientNameChange}
                onSelect={this.onCnCardClientNameSelect}
                options={this.state.existingClientNames}
                disabled={isCnCardFormDisabled}
                description="Use the customer's official company name as found in OP4i"
              />

              <StandardDropdown
                label="Account Type"
                selectedKey={cnCardAccountType}
                onChange={this.onCnCardAccountTypeChange}
                placeholder="Select account type"
                options={accountTypeOptions}
                disabled={isCnCardFormDisabled}
                description="Select Intapp, Partner, or Client"
              />

              <StandardDropdown
                label="Host Site Type"
                selectedKey={cnCardHostSiteType}
                onChange={this.onCnCardHostSiteTypeChange}
                placeholder="Select Host Site Type"
                options={hostSiteTypeOptions}
                disabled={isCnCardFormDisabled}
                description="Select Prod, UAT, or Dev"
              />

              <StandardButton
                text={cnCardSaving ? "Saving..." : "Save"}
                onClick={this.onCnCardSave}
                disabled={
                  isCnCardFormDisabled ||
                  !cnCardClientName.trim() ||
                  !cnCardAccountType ||
                  !cnCardHostSiteType
                }
                primary
                style={{ marginTop: "20px" }}
              />

              {cnCardSaveError && (
                <StatusMessage type="error">{cnCardSaveError}</StatusMessage>
              )}
              {cnCardSaveMessage && (
                <StatusMessage type="success">{cnCardSaveMessage}</StatusMessage>
              )}
            </div>
          )}
        </StandardScreen>
      </PageLayout>
    );
  }
}

export default ManageClient;
